/**
 * 认证服务
 * 封装 IAM API 调用
 */

import { apiClients, TokenManager, ApiClient } from './api'
import type { User } from '@/shared/types/user'
import type { PaginatedResponse } from '@/shared/types/api'
import type {
  LoginRequest,
  RefreshTokenResponseData,
  LogoutResponseData,
} from '@/modules/auth/types/auth'

const REGION_CODE_KEY = 'axiom_region_code'

/**
 * 根据 VITE_REGION 环境变量获取当前部署区域的 region_code
 * overseas → '1'（国际区号）
 * china（默认）→ '86'（中国区号）
 */
export const getRegionCode = (): string => {
  return import.meta.env.VITE_REGION === 'overseas' ? '1' : '86'
}

/**
 * 从 localStorage 读取 region_code，若不存在则从环境变量推导
 */
export const getStoredRegionCode = (): string => {
  return localStorage.getItem(REGION_CODE_KEY) || getRegionCode()
}

/**
 * 认证服务
 */
export const authService = {
  /**
   * 用户登录
   * 符合协议文档：response.data 中包含 access_token, refresh_token, user 等字段
   */
  async login(credentials: LoginRequest): Promise<{ user: User; success: boolean; error?: string }> {
    try {
      console.log('🔐 [Auth] 开始登录请求:', { email: credentials.email })
      
      // 🔑 重要：登录前先清除旧的 Token，确保不会使用旧用户的凭证
      TokenManager.clearTokens()
      console.log('🧹 [Auth] 已清除旧的 Token')
      
      const response = await ApiClient.postWithClient<{
        access_token: string
        refresh_token: string
        token_type: string
        expires_in: number
        user: User
      }>(
        apiClients.iam,
        '/login',
        {
          ...credentials,
          app_id: 'axiom-console'
        }
      )
      
      console.log('✅ [Auth] 登录响应:', response)
      
      if (response.success && response.data) {
        const { user, access_token, refresh_token } = response.data
        console.log('🔑 [Auth] 设置 Token:', { hasAccessToken: !!access_token, hasRefreshToken: !!refresh_token })
        TokenManager.setToken(access_token)
        TokenManager.setRefreshToken(refresh_token)
        localStorage.setItem(REGION_CODE_KEY, getRegionCode())
        return { user, success: true }
      }
      
      console.error('❌ [Auth] 登录失败: response.success =', response.success, 'response.data =', response.data)
      return { user: null as unknown as User, success: false, error: '登录失败，请检查响应格式' }
    } catch (error) {
      console.error('❌ [Auth] 登录异常:', error)
      
      // 如果是网络错误，且是登录失败后的密码修改，不应该显示网络错误
      // 因为密码错误是业务错误，不是网络错误
      if (error instanceof Error && error.name === 'NetworkError') {
        // 检查是否是登录接口的网络错误
        // 如果是，可能是密码错误导致的，返回更友好的错误信息
        const errorMessage = '登录失败，请检查邮箱和密码是否正确'
        return { user: null as unknown as User, success: false, error: errorMessage }
      }
      
      const errorMessage = error instanceof Error ? error.message : '登录失败'
      return { user: null as unknown as User, success: false, error: errorMessage }
    }
  },

  /**
   * 刷新 Token
   */
  async refreshToken(): Promise<boolean> {
    const refreshToken = TokenManager.getRefreshToken()
    if (!refreshToken) {
      return false
    }

    try {
      const response = await ApiClient.postWithClient<RefreshTokenResponseData>(
        apiClients.iam,
        '/refresh',
        { refresh_token: refreshToken }
      )
      
      if (response.success && response.data) {
        TokenManager.setToken(response.data.access_token)
        return true
      }
      
      return false
    } catch {
      TokenManager.clearTokens()
      return false
    }
  },

  /**
   * 用户登出
   */
  async logout(): Promise<void> {
    try {
      await ApiClient.postWithClient<LogoutResponseData>(
        apiClients.iam,
        '/logout',
        {}
      )
    } catch {
      // 忽略登出错误
    } finally {
      TokenManager.clearTokens()
      localStorage.removeItem(REGION_CODE_KEY)
    }
  },

  /**
   * 获取当前用户信息
   */
  async getCurrentUser(): Promise<User | null> {
    if (!TokenManager.hasToken()) {
      return null
    }

    try {
      const response = await ApiClient.getWithClient<User>(
        apiClients.iam,
        '/me'
      )
      
      if (response.success && response.data) {
        return response.data
      }
      
      return null
    } catch {
      return null
    }
  },

  /**
   * 检查认证状态
   */
  async checkAuth(): Promise<{ isAuthenticated: boolean; user: User | null }> {
    if (!TokenManager.hasToken()) {
      return { isAuthenticated: false, user: null }
    }

    const user = await this.getCurrentUser()
    if (user) {
      return { isAuthenticated: true, user }
    }

    // 尝试刷新 Token
    const refreshed = await this.refreshToken()
    if (refreshed) {
      const refreshedUser = await this.getCurrentUser()
      return { isAuthenticated: !!refreshedUser, user: refreshedUser }
    }

    return { isAuthenticated: false, user: null }
  },

  /**
   * 忘记密码（发送验证码）
   */
  async forgotPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await ApiClient.postWithClient<{ message: string }>(
        apiClients.iam,
        '/auth/forgot-password',
        { 
          email,
          app_id: 'axiom-console'
        }
      )
      
      if (response.success) {
        return { success: true }
      }
      
      return { success: false, error: '发送验证码失败' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '发送验证码失败'
      return { success: false, error: errorMessage }
    }
  },

  /**
   * 重置密码
   */
  async resetPassword(data: { email: string; code: string; new_password: string }): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await ApiClient.postWithClient<{ message: string }>(
        apiClients.iam,
        '/auth/reset-password',
        {
          ...data,
          app_id: 'axiom-console'
        }
      )
      
      if (response.success) {
        return { success: true }
      }
      
      return { success: false, error: '重置密码失败' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '重置密码失败'
      return { success: false, error: errorMessage }
    }
  },

  /**
   * 搜索用户（用于Memory模块的UserSearch组件）
   * 需要developer或admin权限
   */
  async searchUsers(params: {
    search?: string
    page?: number
    page_size?: number
    role?: string
    status?: string
  }): Promise<PaginatedResponse<User>> {
    const queryParams = new URLSearchParams()
    if (params.search) queryParams.append('search', params.search)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params.role) queryParams.append('role', params.role)
    if (params.status) queryParams.append('status', params.status)

    const queryString = queryParams.toString()
    return ApiClient.getPaginatedWithClient<User>(
      apiClients.iam,
      `/users${queryString ? `?${queryString}` : ''}`
    )
  },

  // ==================== 用户注册相关 ====================

  /**
   * 发送注册验证码（公开接口）
   */
  async sendCode(email: string, regionCode?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await ApiClient.postWithClient<{ message: string; email: string }>(
        apiClients.iam,
        '/send-code',
        { 
          email, 
          region_code: regionCode || '0086',
          app_id: 'axiom-console'
        }
      )
      
      if (response.success) {
        return { success: true }
      }
      
      return { success: false, error: '发送验证码失败' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '发送验证码失败'
      return { success: false, error: errorMessage }
    }
  },

  /**
   * 用户注册（公开接口）
   */
  async register(data: {
    region_code?: string
    email: string
    code: string
    name: string
    password: string
  }): Promise<{ success: boolean; user_id?: string; error?: string }> {
    try {
      const response = await ApiClient.postWithClient<{
        message: string
        id?: string       // 规范字段名
        user_id?: string  // 兼容旧格式（后端改完后移除）
        email: string
        status: string
      }>(
        apiClients.iam,
        '/register',
        {
          ...data,
          app_id: 'axiom-console'
        }
      )
      
      if (response.success && response.data) {
        localStorage.setItem(REGION_CODE_KEY, getRegionCode())
        // 优先用 id，兼容 user_id（后端改完后移除 user_id）
        return { success: true, user_id: response.data.id || response.data.user_id }
      }
      
      return { success: false, error: '注册失败' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '注册失败'
      return { success: false, error: errorMessage }
    }
  },

  // ==================== 用户管理相关（管理员） ====================

  /**
   * 获取用户列表（管理员接口）
   */
  async getUsers(params?: {
    page?: number
    page_size?: number
    status?: string
    role?: string
    search?: string
  }): Promise<PaginatedResponse<User>> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.role) queryParams.append('role', params.role)
    if (params?.search) queryParams.append('search', params.search)

    const queryString = queryParams.toString()
    return ApiClient.getPaginatedWithClient<User>(
      apiClients.iam,
      `/users${queryString ? `?${queryString}` : ''}`
    )
  },

  /**
   * 更新用户信息（管理员接口）
   * 用于审核用户（修改 status）和修改角色（修改 role）
   */
  async updateUser(
    userId: string,
    data: { status?: string; role?: string; permissions?: string[] }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await ApiClient.patchWithClient<User>(
        apiClients.iam,
        `/users/${userId}`,
        data
      )
      
      if (response.success) {
        return { success: true }
      }
      
      return { success: false, error: '更新用户失败' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '更新用户失败'
      return { success: false, error: errorMessage }
    }
  },

  /**
   * 删除用户（管理员接口）
   */
  async deleteUser(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await ApiClient.deleteWithClient<{ message: string; user_id: string }>(
        apiClients.iam,
        `/users/${userId}`
      )
      
      if (response.success) {
        return { success: true }
      }
      
      return { success: false, error: '删除用户失败' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '删除用户失败'
      return { success: false, error: errorMessage }
    }
  },
}
